import React, { useState, useCallback } from 'react';
import { CloudArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { DOCUMENT_CATEGORIES, CONFIDENTIALITY_LEVELS, SENSITIVITY_FLAGS, CaseFile } from '../types/caseForm';

interface FileUploadProps {
  caseId?: string;
  onFilesSelected?: (files: CaseFile[]) => void;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  className?: string;
}

interface SelectedFile {
  file: File;
  preview?: string;
  id: string;
  category: string;
  description: string;
  documentDate: string;
  authorSource: string;
  language: string;
  confidentiality: string;
  sensitivityWarnings: string[];
  exifStripped: boolean;
  originalFile?: File;
}

const FileUpload: React.FC<FileUploadProps> = ({
  onFilesSelected,
  maxFiles = 10,
  maxFileSize = 50,
  acceptedTypes = [
    'image/jpeg',
    'image/jpg',
    'image/png',
    'image/gif',
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'video/mp4',
    'video/quicktime'
  ],
  className = ''
}) => {
  const [selectedFiles, setSelectedFiles] = useState<SelectedFile[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading] = useState(false);
  const [error, setError] = useState('');
  const [metadataModalFileId, setMetadataModalFileId] = useState<string | null>(null);
  const [stripExif, setStripExif] = useState(false);
  const [strippingExif, setStrippingExif] = useState(false);

  const stripExifBinary = async (file: File): Promise<File> => {
    // Strip EXIF by removing APP1 markers from JPEG binary — no re-encoding, no quality loss
    const buffer = await file.arrayBuffer();
    const view = new DataView(buffer);
    if (view.getUint16(0) !== 0xFFD8) return file; // Not a JPEG

    const chunks: ArrayBuffer[] = [];
    let offset = 0;

    // Keep SOI marker
    chunks.push(buffer.slice(0, 2));
    offset = 2;

    while (offset < buffer.byteLength - 1) {
      const marker = view.getUint16(offset);
      if (marker === 0xFFDA) {
        // Start of scan — rest is image data, keep everything
        chunks.push(buffer.slice(offset));
        break;
      }
      if ((marker & 0xFF00) !== 0xFF00) break; // Invalid marker

      const segmentLength = view.getUint16(offset + 2);
      // Skip APP1 (EXIF) and APP2 (ICC extended) markers
      if (marker === 0xFFE1 || marker === 0xFFE2) {
        offset += 2 + segmentLength;
        continue;
      }
      // Keep all other segments
      chunks.push(buffer.slice(offset, offset + 2 + segmentLength));
      offset += 2 + segmentLength;
    }

    const stripped = new Blob(chunks, { type: 'image/jpeg' });
    return new File([stripped], file.name, { type: file.type, lastModified: file.lastModified });
  };

  const stripExifFromImage = async (file: File): Promise<File> => {
    if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      // Use binary stripping for JPEGs — no quality loss, always smaller
      return stripExifBinary(file);
    }
    // For PNG: use canvas re-encoding (PNGs don't have EXIF in the same way, but may have tEXt chunks)
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) { resolve(file); return; }
        ctx.drawImage(img, 0, 0);
        URL.revokeObjectURL(url);
        canvas.toBlob((blob) => {
          if (!blob) { resolve(file); return; }
          resolve(new File([blob], file.name, { type: 'image/png', lastModified: file.lastModified }));
        }, 'image/png');
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });
  };

  const handleToggleStripExif = async (enabled: boolean) => {
    setStripExif(enabled);
    if (!enabled || selectedFiles.length === 0) return;

    // Re-process existing image files
    setStrippingExif(true);
    try {
      const updated = await Promise.all(
        selectedFiles.map(async (sf) => {
          if (sf.file.type.startsWith('image/') && sf.file.type !== 'image/gif') {
            try {
              const stripped = await stripExifFromImage(sf.file);
              return { ...sf, file: stripped };
            } catch {
              return sf;
            }
          }
          return sf;
        })
      );
      setSelectedFiles(updated);
      if (onFilesSelected) {
        onFilesSelected(updated.map(toCaseFile));
      }
    } finally {
      setStrippingExif(false);
    }
  };

  const validateFile = (file: File): string | null => {
    // Check file type
    if (!acceptedTypes.includes(file.type)) {
      return `File type ${file.type} not allowed`;
    }

    // Check file size
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File size exceeds ${maxFileSize}MB limit`;
    }

    return null;
  };

  const toCaseFile = (f: SelectedFile): CaseFile => ({
    file: f.file, category: f.category, description: f.description, documentDate: f.documentDate,
    authorSource: f.authorSource, language: f.language, confidentiality: f.confidentiality, sensitivityWarnings: f.sensitivityWarnings
  });

  const addFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validFiles: SelectedFile[] = [];
    const errors: string[] = [];

    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
        continue;
      }

      if (selectedFiles.length + validFiles.length >= maxFiles) {
        errors.push(`Maximum ${maxFiles} files allowed`);
        continue;
      }

      // Auto-detect category from file type
      let autoCategory = 'OTH';
      if (file.type.startsWith('image/')) autoCategory = 'PHT';
      else if (file.type.startsWith('video/')) autoCategory = 'VID';
      else if (file.type.startsWith('audio/')) autoCategory = 'AUD';
      else if (file.type === 'application/pdf') autoCategory = 'EVD';
      else if (file.type.includes('document') || file.type.includes('word')) autoCategory = 'RPT';

      // Strip EXIF if enabled and file is an image
      const isStrippableImage = file.type.startsWith('image/') && file.type !== 'image/gif';
      let processedFile = file;
      let didStrip = false;
      if (stripExif && isStrippableImage) {
        try {
          processedFile = await stripExifFromImage(file);
          didStrip = true;
        } catch {
          // If stripping fails, use original
        }
      }

      const selectedFile: SelectedFile = {
        file: processedFile,
        id: `${Date.now()}-${Math.random()}`,
        category: autoCategory,
        description: '',
        documentDate: '',
        authorSource: '',
        language: '',
        confidentiality: 'INTERNAL',
        sensitivityWarnings: [],
        exifStripped: didStrip,
        originalFile: isStrippableImage ? file : undefined
      };

      // Create preview for images
      if (processedFile.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          selectedFile.preview = e.target?.result as string;
          setSelectedFiles(prev =>
            prev.map(f => f.id === selectedFile.id ? selectedFile : f)
          );
        };
        reader.readAsDataURL(processedFile);
      }

      validFiles.push(selectedFile);
    }

    if (errors.length > 0) {
      setError(errors.join(', '));
    } else {
      setError('');
    }

    if (validFiles.length > 0) {
      setSelectedFiles(prev => [...prev, ...validFiles]);
      if (onFilesSelected) {
        const allFiles = [...selectedFiles, ...validFiles];
        onFilesSelected(allFiles.map(toCaseFile));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFiles, maxFiles, maxFileSize, acceptedTypes, onFilesSelected]);

  const removeFile = (fileId: string) => {
    setSelectedFiles(prev => {
      const newFiles = prev.filter(f => f.id !== fileId);
      if (onFilesSelected) {
        onFilesSelected(newFiles.map(toCaseFile));
      }
      return newFiles;
    });
  };

  const toggleSensitivityWarning = (fileId: string, warning: string) => {
    setSelectedFiles(prev => {
      const updated = prev.map(f => {
        if (f.id !== fileId) return f;
        const has = f.sensitivityWarnings.includes(warning);
        return { ...f, sensitivityWarnings: has ? f.sensitivityWarnings.filter(w => w !== warning) : [...f.sensitivityWarnings, warning] };
      });
      if (onFilesSelected) {
        onFilesSelected(updated.map(toCaseFile));
      }
      return updated;
    });
  };

  const toggleFileExifStrip = async (fileId: string) => {
    const sf = selectedFiles.find(f => f.id === fileId);
    if (!sf || !sf.originalFile) return;

    if (sf.exifStripped) {
      // Restore original
      setSelectedFiles(prev => {
        const updated = prev.map(f => f.id === fileId ? { ...f, file: f.originalFile!, exifStripped: false } : f);
        if (onFilesSelected) onFilesSelected(updated.map(toCaseFile));
        return updated;
      });
    } else {
      // Strip
      try {
        const stripped = await stripExifFromImage(sf.originalFile);
        setSelectedFiles(prev => {
          const updated = prev.map(f => f.id === fileId ? { ...f, file: stripped, exifStripped: true } : f);
          if (onFilesSelected) onFilesSelected(updated.map(toCaseFile));
          return updated;
        });
      } catch {
        // Failed, leave as is
      }
    }
  };

  const updateFileField = (fileId: string, field: 'category' | 'description' | 'documentDate' | 'authorSource' | 'language' | 'confidentiality', value: string) => {
    setSelectedFiles(prev => {
      const updated = prev.map(f => f.id === fileId ? { ...f, [field]: value } : f);
      if (onFilesSelected) {
        onFilesSelected(updated.map(toCaseFile));
      }
      return updated;
    });
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addFiles(e.dataTransfer.files);
    }
  }, [addFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(e.target.files);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return '🖼️';
    } else if (mimeType === 'application/pdf') {
      return '📄';
    } else if (mimeType.includes('document') || mimeType.includes('word')) {
      return '📝';
    } else if (mimeType.includes('sheet') || mimeType.includes('excel')) {
      return '📊';
    } else if (mimeType.startsWith('video/')) {
      return '🎥';
    } else if (mimeType.startsWith('audio/')) {
      return '🎵';
    }
    return '📎';
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Upload Area */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
          dragActive
            ? 'border-emerald-500 bg-emerald-50'
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />

        <div className="text-center">
          <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
          <div className="mt-4">
            <p className="text-lg font-medium text-gray-900 dark:text-gray-100">
              Upload documents
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Drag and drop files here, or click to select files
            </p>
          </div>
          <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
            <p>Maximum {maxFiles} files, {maxFileSize}MB each</p>
            <p>Supported: Images, PDFs, Documents, Videos</p>
          </div>
        </div>
      </div>

      {/* Strip EXIF toggle */}
      <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg">
        <div className="flex items-start gap-2">
          <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-amber-800">Remove location data from images</p>
            <p className="text-xs text-amber-700">Strips EXIF metadata (GPS coordinates, device info) from uploaded images for safety</p>
          </div>
        </div>
        <button
          type="button"
          disabled={strippingExif}
          onClick={() => handleToggleStripExif(!stripExif)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 ${stripExif ? 'bg-emerald-600' : 'bg-gray-200 dark:bg-gray-600'} ${strippingExif ? 'opacity-50' : ''}`}
        >
          <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${stripExif ? 'translate-x-5' : 'translate-x-0'}`} />
        </button>
      </div>
      {strippingExif && (
        <div className="flex items-center gap-2 text-sm text-amber-700">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600"></div>
          Stripping metadata from images...
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}

      {/* Selected Files */}
      {selectedFiles.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Selected Files ({selectedFiles.length})
          </h4>
          <div className="space-y-2">
            {selectedFiles.map((selectedFile) => (
              <div key={selectedFile.id} className="p-3 bg-gray-50 dark:bg-gray-900 rounded-lg space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {selectedFile.preview ? (
                      <img
                        src={selectedFile.preview}
                        alt="Preview"
                        className="h-10 w-10 object-cover rounded"
                      />
                    ) : (
                      <div className="h-10 w-10 flex items-center justify-center bg-gray-200 dark:bg-gray-600 rounded text-lg">
                        {getFileIcon(selectedFile.file.type)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {selectedFile.file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(selectedFile.file.size)}
                        {selectedFile.originalFile && (
                          <button
                            type="button"
                            onClick={() => toggleFileExifStrip(selectedFile.id)}
                            className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                              selectedFile.exifStripped
                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                            }`}
                            title={selectedFile.exifStripped ? 'EXIF stripped — click to restore' : 'EXIF intact — click to strip'}
                          >
                            {selectedFile.exifStripped ? 'EXIF stripped' : 'EXIF intact'}
                          </button>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setMetadataModalFileId(selectedFile.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50 font-medium rounded border border-emerald-300"
                    >
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                      </svg>
                      Add Metadata
                    </button>
                    <button
                      type="button"
                      onClick={() => removeFile(selectedFile.id)}
                      className="text-gray-400 hover:text-red-500"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-28 flex-shrink-0">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Category *</label>
                    <select
                      value={selectedFile.category}
                      onChange={(e) => updateFileField(selectedFile.id, 'category', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                    >
                      {DOCUMENT_CATEGORIES.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="w-36 flex-shrink-0">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Date *</label>
                    <input
                      type="date"
                      value={selectedFile.documentDate}
                      onChange={(e) => updateFileField(selectedFile.id, 'documentDate', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-0.5">Description * <span className="text-gray-400">{selectedFile.description.length}/50</span></label>
                    <input
                      type="text"
                      maxLength={50}
                      value={selectedFile.description}
                      onChange={(e) => updateFileField(selectedFile.id, 'description', e.target.value)}
                      placeholder="Brief description"
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                </div>
                {/* Indicator badges for filled metadata */}
                {(selectedFile.sensitivityWarnings.length > 0 || selectedFile.authorSource || selectedFile.language || selectedFile.confidentiality !== 'INTERNAL') && (
                  <div className="flex flex-wrap gap-1">
                    {selectedFile.sensitivityWarnings.map(w => {
                      const flag = SENSITIVITY_FLAGS.find(f => f.value === w);
                      return flag ? (
                        <span key={w} className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                          {flag.prefix}
                        </span>
                      ) : null;
                    })}
                    {selectedFile.confidentiality !== 'INTERNAL' && (
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${selectedFile.confidentiality === 'RESTRICTED' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                        {selectedFile.confidentiality}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Status */}
      {uploading && (
        <div className="rounded-md bg-blue-50 p-4">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-sm text-blue-800">Uploading files...</span>
          </div>
        </div>
      )}

      {/* Metadata Modal */}
      {metadataModalFileId && (() => {
        const modalFile = selectedFiles.find(f => f.id === metadataModalFileId);
        if (!modalFile) return null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setMetadataModalFileId(null)} />
            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Document Details</h3>
                <button
                  type="button"
                  onClick={() => setMetadataModalFileId(null)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 truncate">{modalFile.file.name}</p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Author / Source</label>
                  <input
                    type="text"
                    value={modalFile.authorSource}
                    onChange={(e) => updateFileField(modalFile.id, 'authorSource', e.target.value)}
                    placeholder="e.g. Maria Santos, IPRI field office"
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Language</label>
                    <input
                      type="text"
                      value={modalFile.language}
                      onChange={(e) => updateFileField(modalFile.id, 'language', e.target.value)}
                      placeholder="e.g. Spanish"
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confidentiality</label>
                    <select
                      value={modalFile.confidentiality}
                      onChange={(e) => updateFileField(modalFile.id, 'confidentiality', e.target.value)}
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 text-sm"
                    >
                      {CONFIDENTIALITY_LEVELS.map(lvl => (
                        <option key={lvl.value} value={lvl.value}>{lvl.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Sensitivity Warnings</label>
                  <div className="space-y-2">
                    {SENSITIVITY_FLAGS.map(flag => (
                      <label key={flag.value} className="flex items-start gap-2">
                        <input
                          type="checkbox"
                          checked={modalFile.sensitivityWarnings.includes(flag.value)}
                          onChange={() => toggleSensitivityWarning(modalFile.id, flag.value)}
                          className="mt-0.5 rounded border-gray-300 text-red-600 shadow-sm focus:border-red-500 focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{flag.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  onClick={() => setMetadataModalFileId(null)}
                  className="w-full px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default FileUpload;